import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Save, Plus, Trash2 } from 'lucide-react';

const FooterTab = ({
  footerColumns,
  setFooterColumns,
  socialLinks,
  setSocialLinks,
  appLinks,
  setAppLinks,
  saving,
  onSave
}) => {
  const addFooterColumn = () => {
    const newCol = {
      id: `footer-${Date.now()}`,
      title: 'New Column',
      order: footerColumns.length + 1,
      links: []
    };
    setFooterColumns([...footerColumns, newCol]);
  };

  const updateFooterColumn = (id, field, value) => {
    setFooterColumns(footerColumns.map(col => col.id === id ? { ...col, [field]: value } : col));
  };

  const deleteFooterColumn = (id) => {
    setFooterColumns(footerColumns.filter(col => col.id !== id));
  };

  const addFooterLink = (columnId) => {
    setFooterColumns(footerColumns.map(col => {
      if (col.id === columnId) {
        return { ...col, links: [...col.links, { label: 'New Link', url: '/' }] };
      }
      return col;
    }));
  };

  const updateFooterLink = (columnId, linkIndex, field, value) => {
    setFooterColumns(footerColumns.map(col => {
      if (col.id === columnId) {
        const newLinks = [...col.links];
        newLinks[linkIndex] = { ...newLinks[linkIndex], [field]: value };
        return { ...col, links: newLinks };
      }
      return col;
    }));
  };

  const deleteFooterLink = (columnId, linkIndex) => {
    setFooterColumns(footerColumns.map(col => {
      if (col.id === columnId) {
        return { ...col, links: col.links.filter((_, i) => i !== linkIndex) };
      }
      return col;
    }));
  };

  const updateSocialLink = (platform, field, value) => {
    setSocialLinks(socialLinks.map(link => link.platform === platform ? { ...link, [field]: value } : link));
  };

  return (
    <div className="space-y-6">
      {/* Footer Columns */}
      <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">Footer Columns</h2>
          <div className="flex gap-2">
            <Button onClick={addFooterColumn} variant="outline" className="border-zinc-700 text-zinc-300">
              <Plus className="w-4 h-4 mr-2" />
              Add Column
            </Button>
            <Button onClick={onSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {footerColumns.map((col) => (
            <div key={col.id} className="bg-zinc-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <Input
                  value={col.title}
                  onChange={(e) => updateFooterColumn(col.id, 'title', e.target.value)}
                  className="bg-zinc-700 border-zinc-600 text-white font-semibold"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteFooterColumn(col.id)}
                  className="text-red-400 hover:text-red-300 ml-2"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-2">
                {col.links.map((link, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      value={link.label}
                      onChange={(e) => updateFooterLink(col.id, idx, 'label', e.target.value)}
                      className="bg-zinc-700 border-zinc-600 text-white text-sm"
                      placeholder="Label"
                    />
                    <Input
                      value={link.url}
                      onChange={(e) => updateFooterLink(col.id, idx, 'url', e.target.value)}
                      className="bg-zinc-700 border-zinc-600 text-white text-sm"
                      placeholder="URL"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteFooterLink(col.id, idx)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => addFooterLink(col.id)}
                  className="w-full border-zinc-600 text-zinc-400 mt-2"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add Link
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Social Links */}
      <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
        <h2 className="text-xl font-semibold text-white mb-4">Social Media Links</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {socialLinks.map((link) => (
            <div key={link.platform} className="flex items-center gap-4 bg-zinc-800 rounded-lg p-3">
              <span className="text-white capitalize w-24">{link.platform}</span>
              <Input
                value={link.url}
                onChange={(e) => updateSocialLink(link.platform, 'url', e.target.value)}
                className="bg-zinc-700 border-zinc-600 text-white flex-1"
                placeholder="https://..."
              />
              <Switch
                checked={link.active}
                onCheckedChange={(checked) => updateSocialLink(link.platform, 'active', checked)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* App Download Links */}
      <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
        <h2 className="text-xl font-semibold text-white mb-4">App Download Links</h2>
        <p className="text-zinc-500 text-sm mb-4">Google Play and App Store buttons will appear under the logo in the footer</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-zinc-300">Google Play (Android) URL</Label>
            <Input
              value={appLinks?.android_url || ''}
              onChange={(e) => setAppLinks({ ...appLinks, android_url: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-white"
              placeholder="https://play.google.com/store/apps/details?id=..."
            />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300">App Store (iOS) URL</Label>
            <Input
              value={appLinks?.ios_url || ''}
              onChange={(e) => setAppLinks({ ...appLinks, ios_url: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-white"
              placeholder="https://apps.apple.com/app/..."
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FooterTab;
